(() => {
  const MAX_FILE_SIZE = 15 * 1024 * 1024;
  const OUTPUT_LONG_EDGE = 5400;
  const MIN_CROP_WIDTH = 80;

  class UploadPreview {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.uploadPreviewMode || 'section';
      this.input = root.querySelector('[data-upload-input]');
      this.croppedInput = root.querySelector('[data-cropped-upload-input]');
      this.preview = root.querySelector('[data-preview-art]') || root.querySelector('.jq-mockup__art');
      this.previewThumb = root.querySelector('[data-preview-thumb]') || root.querySelector('.jq-product__thumb--mockup img');
      this.poster = root.querySelector('[data-preview-poster]') || root.querySelector('[data-mockup-print]');
      this.scene = root.querySelector('[data-preview-scene]') || root.querySelector('[data-product-mockup]');
      this.status = root.querySelector('[data-upload-status]');
      this.dropZone = root.querySelector('[data-upload-dropzone]');
      this.variantInput = root.querySelector('[data-upload-variant-id]');
      this.orderForm = root.querySelector('[data-upload-order-form]');
      this.submitButton = root.querySelector('[data-upload-submit]');
      this.cropProperty = root.querySelector('[data-crop-property]');
      this.dialog = root.querySelector('[data-crop-dialog]');
      this.canvas = root.querySelector('[data-crop-canvas]');
      this.context = this.canvas?.getContext('2d');
      this.stage = root.querySelector('[data-crop-stage]');
      this.cropBox = root.querySelector('[data-crop-box]');
      this.ratioInputs = [...root.querySelectorAll('[data-crop-ratio]')];
      this.appliedImage = null;
      this.image = null;
      this.imageRect = null;
      this.crop = null;
      this.drag = null;
      this.previewRatio = null;
      this.displayRatio = null;
      this.resizeObserver = null;
      this.sourceFile = null;
      this.pendingSourceFile = null;
      this.appliedFile = null;
      this.hasPendingCrop = false;
      this.variantAvailable = true;
      const variantData = root.querySelector('[data-upload-variants]');
      this.materialVariants = variantData ? JSON.parse(variantData.textContent) : null;

      // Keep the Shopify variant and quantity fields valid even if the cropper
      // cannot initialize (for example while a section is being re-rendered).
      // The commerce submit handler runs at the document level, so this capture
      // listener guarantees the selected size is synced first.
      this.bindOrderForm();

      if (
        !this.input ||
        !this.croppedInput ||
        !this.preview ||
        !this.poster ||
        !this.dialog ||
        !this.canvas ||
        !this.context ||
        !this.stage ||
        !this.cropBox
      ) {
        return;
      }

      this.bind();
    }

    syncSelectedVariant(selected) {
      if (this.materialVariants) {
        const material = this.root.querySelector('[data-preview-material]:checked')?.value;
        const frame = this.root.querySelector('[data-preview-frame]:checked')?.value || 'Zonder lijst';
        const mat = this.root.querySelector('[data-preview-mat]:checked')?.value || 'Zonder';
        const finish = material === 'Canvas' ? 'Inclusief frame' : mat === 'Zonder' ? frame : `${frame} / ${mat}`;
        const variant = this.materialVariants.find(item => item.option1 === selected?.value && item.option2 === material && item.option3 === finish);
        const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {style:'currency',currency:this.root.dataset.previewCurrency || 'EUR'}).format(amount / 100);
        const find = finish => this.materialVariants.find(item => item.option1 === selected?.value && item.option2 === 'Poster' && item.option3 === finish);
        const base = find('Zonder lijst');
        this.root.querySelectorAll('[data-preview-frame]').forEach(input => {
          const price = input.closest('label')?.querySelector('[data-preview-addon-price]');
          const framed = find(input.value);
          if (price) price.textContent = framed && base ? '+' + money(framed.price - base.price) : '';
        });
        this.root.querySelectorAll('[data-preview-mat]').forEach(input => {
          const price = input.closest('label')?.querySelector('[data-preview-addon-price]');
          const framed = find(frame);
          const matted = find(`${frame} / ${input.value}`);
          if (price) price.textContent = framed && matted ? '+' + money(matted.price - framed.price) : input.dataset.previewMatSize === '3' ? '+' + money(400) : '+' + money(600);
        });
        const total = this.root.querySelector('[data-preview-price]');
        if (total) total.textContent = variant ? money(variant.price) : '';
        if (this.variantInput) this.variantInput.value = variant ? String(variant.id) : '';
        this.variantAvailable = Boolean(variant?.available);
      } else {
        if (this.variantInput && selected?.dataset.variantId) this.variantInput.value = selected.dataset.variantId;
        this.variantAvailable = selected?.dataset.variantAvailable !== 'false';
      }
    }

    syncOrderForm() {
      if (!this.orderForm) return;
      const selected = [...this.root.querySelectorAll('input[data-preview-size], input[data-mockup-size]')]
        .find((input) => input.checked);
      this.syncSelectedVariant(selected);
      const quantity = this.orderForm.querySelector('input[name="quantity"]');
      if (quantity && (!quantity.value || Number(quantity.value) < 1)) quantity.value = '1';
    }

    bindOrderForm() {
      if (!this.orderForm || this.orderForm.dataset.uploadOrderFormBound === 'true') return;
      this.orderForm.dataset.uploadOrderFormBound = 'true';
      this.orderForm.addEventListener('submit', () => this.syncOrderForm(), { capture: true });
      this.syncOrderForm();
    }

    bind() {
      this.input.addEventListener('change', () => this.readFile());

      if (this.dropZone) {
        ['dragenter', 'dragover'].forEach((eventName) => {
          this.dropZone.addEventListener(eventName, (event) => {
            event.preventDefault();
            this.dropZone.classList.add('is-dragging');
          });
        });
        ['dragleave', 'drop'].forEach((eventName) => {
          this.dropZone.addEventListener(eventName, (event) => {
            event.preventDefault();
            this.dropZone.classList.remove('is-dragging');
          });
        });
        this.dropZone.addEventListener('drop', (event) => {
          const file = [...(event.dataTransfer?.files || [])].find((item) => item.type.startsWith('image/'));
          if (!file) {
            this.setStatus('Sleep een JPG-, PNG- of WebP-afbeelding naar dit vlak.', true);
            return;
          }
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.input.files = transfer.files;
          this.readFile();
        });
      }

      this.root.querySelectorAll('input[data-preview-size], input[data-mockup-size]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.syncSize();
        });
      });

      this.root.querySelectorAll('[data-preview-material]').forEach(input => {
        input.addEventListener('change', () => { this.syncFrame(); this.syncSize(); });
      });

      this.root.querySelectorAll('[data-preview-frame]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.syncFrame();
        });
      });

      this.root.querySelectorAll('[data-preview-mat]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.syncFrame();
        });
      });

      this.ratioInputs.forEach(input => input.addEventListener('change', () => {
        if (input.checked) this.setCropRatio(input.value);
      }));
      this.root.querySelector('[data-recrop]')?.addEventListener('click', () => this.reopenCrop());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      });
      this.input.form?.addEventListener('submit', (event) => {
        if (!this.cropIsValid() || this.hasPendingCrop) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setStatus('Bevestig eerst je uitsnede.', true);
          this.reopenCrop();
        }
      }, { capture: true });

      this.root.querySelectorAll('[data-crop-cancel]').forEach((button) => {
        button.addEventListener('click', () => this.closeDialog());
      });

      this.root.querySelector('[data-crop-apply]')?.addEventListener('click', () => this.applyCrop());
      this.stage.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.stage.addEventListener('pointermove', (event) => this.moveDrag(event));
      this.stage.addEventListener('pointerup', (event) => this.endDrag(event));
      this.stage.addEventListener('pointercancel', (event) => this.endDrag(event));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });

      if (this.mode !== 'product' && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncPosterGeometry());
        this.resizeObserver.observe(this.scene);
      }

      this.syncSize();
      this.syncFrame();
      this.syncSubmitState();
    }

    setStatus(message = '', isError = false) {
      this.status.textContent = message;
      this.status.toggleAttribute('data-error', isError);
    }

    cropIsValid() {
      return Boolean(this.appliedFile);
    }

    syncSubmitState() {
      const valid = this.cropIsValid() && !this.hasPendingCrop;
      this.input?.setCustomValidity(valid ? '' : 'Bevestig eerst je uitsnede.');
      if (this.submitButton) this.submitButton.disabled = !valid || !this.variantAvailable;
      const recrop = this.root.querySelector('[data-recrop]');
      if (recrop) { recrop.hidden = !this.sourceFile; recrop.style.display = this.sourceFile ? '' : 'none'; }

    }

    reopenCrop() {
      if (!this.sourceFile || !this.appliedImage) return;
      this.image = this.appliedImage;
      this.pendingSourceFile = this.sourceFile;
      this.hasPendingCrop = true;
      this.syncSubmitState();
      this.drawStage();
      this.setCropRatio(this.ratioInputs.find(input => input.checked)?.value || '0.6666667');
      this.openDialog();
    }

    setInputFile(input, file) {
      if (!file) {
        input.value = '';
        return;
      }

      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
    }

    restoreAppliedFile() {
      this.setInputFile(this.input, this.sourceFile);
      this.setInputFile(this.croppedInput, this.appliedFile);
      if (this.sourceFile) {
        this.input.setCustomValidity('');
      }
      this.pendingSourceFile = null;
      this.hasPendingCrop = false;
      this.image = this.appliedImage;
      this.syncSubmitState();
    }

    syncSize() {
      const inputs = [...this.root.querySelectorAll('input[data-preview-size], input[data-mockup-size]')];
      const selected = inputs.find((input) => input.checked) || inputs[0];
      const parse = (value) => {
        const numbers = String(value || '').match(/\d+(?:[.,]\d+)?/g);
        if (!numbers || numbers.length < 2) return null;

        const width = Number(numbers[0].replace(',', '.'));
        const height = Number(numbers[1].replace(',', '.'));
        return width > 0 && height > 0 ? { width, height, longest: Math.max(width, height) } : null;
      };
      const size = parse(selected?.value);
      const available = inputs.map((input) => parse(input.value)).filter(Boolean);

      this.syncSelectedVariant(selected);
      this.syncSubmitState();
      if (this.appliedFile && !this.variantAvailable) {
        this.setStatus('Je kunt het voorbeeld bekijken. Bestellen is alleen beschikbaar voor levering in Nederland.');
      }

      if (!size || !available.length) return;

      this.selectedRatio = size.width / size.height;
      this.selectedSize = selected.value;
      this.root.querySelectorAll('[data-crop-size]').forEach(el => { el.textContent = selected.value; });
      this.syncSubmitState();

      const maximum = Math.max(...available.map((item) => item.longest));
      this.selectedSizeScale = Math.sqrt(size.longest / maximum);
      const previewRatio = this.previewRatio || this.selectedRatio;
      this.displayRatio = previewRatio;
      this.poster.style.setProperty('--preview-ratio', String(previewRatio));
      this.syncPosterGeometry(previewRatio);

      if (this.scene) {
        this.scene.dataset.selectedSize = selected.value;
      }
    }

    syncFrame() {
      const frameInputs = [...this.root.querySelectorAll('[data-preview-frame]')];
      const matInputs = [...this.root.querySelectorAll('[data-preview-mat]')];
      const canvas = this.root.querySelector('[data-preview-material]:checked')?.value === 'Canvas';
      if (canvas) frameInputs.find(input => input.dataset.previewFrameKey === 'none').checked = true;
      const accessories = this.root.querySelector('[data-preview-accessories]');
      if (accessories) accessories.hidden = canvas;
      const note = this.root.querySelector('[data-preview-canvas-note]');
      if (note) note.hidden = !canvas;
      const selectedFrame = frameInputs.find((input) => input.checked) || frameInputs[0];
      if (!selectedFrame) return;

      const frameKey = selectedFrame.dataset.previewFrameKey || selectedFrame.value;
      const withoutFrame = frameKey === 'none';
      this.poster.dataset.frame = frameKey;

      matInputs.forEach((input) => {
        if (input.dataset.previewMatSize !== '0') input.disabled = withoutFrame;
      });

      if (withoutFrame) {
        const withoutMat = matInputs.find((input) => input.dataset.previewMatSize === '0');
        if (withoutMat) withoutMat.checked = true;
      }

      const selectedMat = matInputs.find((input) => input.checked) || matInputs[0];
      if (selectedMat) {
        this.poster.dataset.mat = selectedMat.dataset.previewMatSize || selectedMat.value;
      }
      this.syncOrderForm();
      this.syncSubmitState();
    }

    syncPosterGeometry(ratio = this.displayRatio) {
      if (this.mode === 'product') return;
      if (!this.scene || !Number.isFinite(ratio) || ratio <= 0) return;

      const sceneWidth = this.scene.clientWidth;
      const sceneHeight = this.scene.clientHeight;
      if (!sceneWidth || !sceneHeight) return;

      let minimumWidth = 180;
      let maximumWidth = 220;
      let widthRatio = 0.2;

      if (window.matchMedia('(max-width: 639px)').matches) {
        minimumWidth = 120;
        maximumWidth = 145;
        widthRatio = 0.34;
      } else if (window.matchMedia('(max-width: 989px)').matches) {
        minimumWidth = 150;
        maximumWidth = 200;
        widthRatio = 0.24;
      }

      const isLandscape = ratio > 1.05;
      const sizeScale = Number.isFinite(this.selectedSizeScale) ? this.selectedSizeScale : 1;
      const preferredWidth = this.clamp(sceneWidth * widthRatio, minimumWidth, maximumWidth)
        * sizeScale
        * (isLandscape ? 1.35 : 1);
      const safeHeight = sceneHeight * 0.75;
      const safeWidthForRatio = safeHeight * ratio;
      const posterWidth = Math.min(preferredWidth, safeWidthForRatio);

      this.poster.style.setProperty('--preview-poster-width', `${posterWidth.toFixed(2)}px`);
      this.poster.dataset.orientation = isLandscape ? 'landscape' : ratio < 0.95 ? 'portrait' : 'square';
    }

    readFile() {
      const file = this.input.files?.[0];
      this.setStatus();
      this.input.setCustomValidity('');

      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.setStatus('Kies een JPG-, PNG- of WebP-afbeelding.', true);
        this.input.setCustomValidity('Kies een JPG-, PNG- of WebP-afbeelding.');
        this.restoreAppliedFile();
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.setStatus('De afbeelding is groter dan 15 MB.', true);
        this.input.setCustomValidity('De afbeelding mag maximaal 15 MB zijn.');
        this.restoreAppliedFile();
        return;
      }

      this.pendingSourceFile = file;
      this.hasPendingCrop = true;
      this.syncSubmitState();

      const reader = new FileReader();
      reader.onerror = () => {
        this.setStatus('De afbeelding kon niet worden gelezen.', true);
        this.restoreAppliedFile();
      };
      reader.onload = () => {
        const image = new Image();
        image.onerror = () => {
          this.setStatus('De afbeelding kon niet worden geopend.', true);
          this.restoreAppliedFile();
        };
        image.onload = () => {
          this.image = image;
          this.drawStage();
          this.setCropRatio(this.ratioInputs.find(input => input.checked)?.value || '0.6666667');
          this.openDialog();
        };
        image.src = reader.result;
      };
      reader.readAsDataURL(file);
    }

    drawStage() {
      if (!this.image) return;

      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;
      const scale = Math.min(
        canvasWidth / this.image.naturalWidth,
        canvasHeight / this.image.naturalHeight
      );
      const width = this.image.naturalWidth * scale;
      const height = this.image.naturalHeight * scale;
      const x = (canvasWidth - width) / 2;
      const y = (canvasHeight - height) / 2;

      this.imageRect = { x, y, width, height };
      this.context.fillStyle = '#20201f';
      this.context.fillRect(0, 0, canvasWidth, canvasHeight);
      this.context.drawImage(this.image, x, y, width, height);
    }

    setCropRatio(value) {
      if (!this.imageRect) return;

      const isFree = value === 'free';
      const numericRatio = Number(value);
      if (!isFree && (!Number.isFinite(numericRatio) || numericRatio <= 0)) return;
      const ratio = isFree
        ? (this.crop?.width && this.crop?.height ? this.crop.width / this.crop.height : this.imageRect.width / this.imageRect.height)
        : numericRatio;

      const availableWidth = this.imageRect.width * 0.76;
      const availableHeight = this.imageRect.height * 0.76;
      let width = availableWidth;
      let height = width / ratio;

      if (height > availableHeight) {
        height = availableHeight;
        width = height * ratio;
      }

      this.crop = {
        x: this.imageRect.x + (this.imageRect.width - width) / 2,
        y: this.imageRect.y + (this.imageRect.height - height) / 2,
        width,
        height,
        ratio: isFree ? null : ratio,
      };
      this.updateCropBox();
    }

    updateCropBox() {
      if (!this.crop) return;

      this.cropBox.style.left = `${(this.crop.x / this.canvas.width) * 100}%`;
      this.cropBox.style.top = `${(this.crop.y / this.canvas.height) * 100}%`;
      this.cropBox.style.width = `${(this.crop.width / this.canvas.width) * 100}%`;
      this.cropBox.style.height = `${(this.crop.height / this.canvas.height) * 100}%`;
    }

    openDialog() {
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      requestAnimationFrame(() => this.updateCropBox());
    }

    closeDialog(clearInput = true) {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      this.drag = null;
      if (clearInput) {
        this.restoreAppliedFile();
      }
    }

    getCanvasPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (this.canvas.width / rect.width),
        y: (event.clientY - rect.top) * (this.canvas.height / rect.height),
      };
    }

    startDrag(event) {
      if (!this.crop || !event.target.closest('[data-crop-box]')) return;

      event.preventDefault();
      const handle = event.target.closest('[data-crop-handle]')?.dataset.cropHandle;
      this.stage.setPointerCapture(event.pointerId);
      this.drag = {
        mode: handle ? 'resize' : 'move',
        handle,
        point: this.getCanvasPoint(event),
        crop: { ...this.crop },
      };
      this.cropBox.dataset.dragging = 'true';
    }

    moveDrag(event) {
      if (!this.drag || !this.imageRect) return;

      event.preventDefault();
      const point = this.getCanvasPoint(event);
      if (this.drag.mode === 'move') {
        this.moveCrop(point);
      } else {
        this.resizeCrop(point);
      }
      this.updateCropBox();
    }

    moveCrop(point) {
      const start = this.drag.crop;
      const deltaX = point.x - this.drag.point.x;
      const deltaY = point.y - this.drag.point.y;
      const minX = this.imageRect.x;
      const maxX = this.imageRect.x + this.imageRect.width - start.width;
      const minY = this.imageRect.y;
      const maxY = this.imageRect.y + this.imageRect.height - start.height;

      this.crop = {
        ...start,
        x: this.clamp(start.x + deltaX, minX, maxX),
        y: this.clamp(start.y + deltaY, minY, maxY),
      };
    }

    resizeCrop(point) {
      const start = this.drag.crop;
      const handle = this.drag.handle;
      const growsRight = handle.includes('e');
      const growsDown = handle.includes('s');
      const fixedX = growsRight ? start.x : start.x + start.width;
      const fixedY = growsDown ? start.y : start.y + start.height;
      const maximumWidthX = growsRight
        ? this.imageRect.x + this.imageRect.width - fixedX
        : fixedX - this.imageRect.x;
      const maximumHeightY = growsDown
        ? this.imageRect.y + this.imageRect.height - fixedY
        : fixedY - this.imageRect.y;

      if (!Number.isFinite(start.ratio)) {
        const minimumWidth = Math.min(MIN_CROP_WIDTH, maximumWidthX);
        const minimumHeight = Math.min(MIN_CROP_WIDTH, maximumHeightY);
        const width = this.clamp(Math.abs(point.x - fixedX), minimumWidth, maximumWidthX);
        const height = this.clamp(Math.abs(point.y - fixedY), minimumHeight, maximumHeightY);

        this.crop = {
          ratio: null,
          width,
          height,
          x: growsRight ? fixedX : fixedX - width,
          y: growsDown ? fixedY : fixedY - height,
        };
        return;
      }

      const maximumWidth = Math.min(maximumWidthX, maximumHeightY * start.ratio);
      const requestedWidth = Math.max(
        Math.abs(point.x - fixedX),
        Math.abs(point.y - fixedY) * start.ratio
      );
      const minimumWidth = Math.min(MIN_CROP_WIDTH, maximumWidth);
      const width = this.clamp(requestedWidth, minimumWidth, maximumWidth);
      const height = width / start.ratio;

      this.crop = {
        ratio: start.ratio,
        width,
        height,
        x: growsRight ? fixedX : fixedX - width,
        y: growsDown ? fixedY : fixedY - height,
      };
    }

    endDrag(event) {
      if (!this.drag) return;
      if (this.stage.hasPointerCapture(event.pointerId)) {
        this.stage.releasePointerCapture(event.pointerId);
      }
      this.drag = null;
      delete this.cropBox.dataset.dragging;
    }

    applyCrop() {
      if (!this.image || !this.imageRect || !this.crop) return;

      const sourceScaleX = this.image.naturalWidth / this.imageRect.width;
      const sourceScaleY = this.image.naturalHeight / this.imageRect.height;
      const sourceX = (this.crop.x - this.imageRect.x) * sourceScaleX;
      const sourceY = (this.crop.y - this.imageRect.y) * sourceScaleY;
      const sourceWidth = this.crop.width * sourceScaleX;
      const sourceHeight = this.crop.height * sourceScaleY;
      const output = document.createElement('canvas');
      const cropRatio = sourceWidth / sourceHeight;
      const sourceLongEdge = Math.max(sourceWidth, sourceHeight);
      const outputLongEdge = Math.min(OUTPUT_LONG_EDGE, Math.max(1, Math.round(sourceLongEdge)));
      output.width = cropRatio >= 1 ? outputLongEdge : Math.round(outputLongEdge * cropRatio);
      output.height = cropRatio >= 1 ? Math.round(outputLongEdge / cropRatio) : outputLongEdge;
      const context = output.getContext('2d');

      if (!context) return;

      context.drawImage(
        this.image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        output.width,
        output.height
      );

      const previewUrl = output.toDataURL('image/jpeg', 0.9);
      const finish = (file = null) => {
        if (file) {
          this.sourceFile = this.pendingSourceFile || this.input.files?.[0] || null;
          this.appliedFile = file;
          this.appliedImage = this.image;
          this.setInputFile(this.input, this.sourceFile);
          this.setInputFile(this.croppedInput, this.appliedFile);
        }
        this.pendingSourceFile = null;
        this.hasPendingCrop = false;
        this.syncSubmitState();

        this.previewRatio = cropRatio;
        this.displayRatio = cropRatio;
        this.poster.dataset.cropped = 'true';
        this.poster.style.setProperty('--preview-ratio', String(cropRatio));
        if (this.mode === 'product') {
          this.scene.style.setProperty('--jq-mockup-ratio', String(cropRatio));
          this.root.querySelectorAll('[data-product-media]').forEach((media) => {
            const active = media.dataset.mediaId === 'mockup';
            media.hidden = !active;
            media.classList.toggle('is-active', active);
          });
          this.root.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
            const active = thumb.dataset.mediaTarget === 'mockup';
            thumb.classList.toggle('is-active', active);
            thumb.setAttribute('aria-pressed', String(active));
          });
        } else {
          this.syncPosterGeometry(cropRatio);
        }
        this.preview.src = previewUrl;
        this.preview.removeAttribute('srcset');
        this.preview.removeAttribute('sizes');
        if (this.previewThumb) {
          this.previewThumb.src = previewUrl;
          this.previewThumb.removeAttribute('srcset');
          this.previewThumb.removeAttribute('sizes');
        }
        if (this.cropProperty) this.cropProperty.value = this.ratioInputs.find(input => input.checked)?.dataset.cropLabel || 'Vrije uitsnede';
        this.input.setCustomValidity('');
        const successMessage = this.mode === 'product'
          ? 'Je afbeelding is bijgesneden en klaar om te bestellen.'
          : 'Je afbeelding is bijgesneden. Je kunt nu bestellen.';
        this.setStatus(
          this.variantAvailable
            ? successMessage
            : 'Je afbeelding is bijgesneden. Bestellen is alleen beschikbaar voor levering in Nederland.'
        );
        this.closeDialog(false);
      };

      output.toBlob((blob) => {
        if (!blob) {
          this.setStatus('De uitsnede kon niet worden voorbereid. Probeer het opnieuw.', true);
          this.restoreAppliedFile();
          return;
        }
        try {
          const sourceName = this.pendingSourceFile?.name || this.input.files?.[0]?.name || 'eigen-poster';
          const baseName = sourceName.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').slice(0, 60) || 'eigen-poster';
          finish(new File([blob], `${baseName}-uitsnede.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
        } catch {
          this.setStatus('De uitsnede kon niet aan het winkelmandje worden gekoppeld. Probeer een andere browser.', true);
          this.restoreAppliedFile();
        }
      }, 'image/jpeg', 0.92);
    }

    clamp(value, minimum, maximum) {
      return Math.max(minimum, Math.min(maximum, value));
    }

    destroy() {
      this.resizeObserver?.disconnect();
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-upload-preview]').forEach((root) => {
      if (root.dataset.uploadPreviewReady === 'true') return;
      root.dataset.uploadPreviewReady = 'true';
      root.uploadPreviewInstance = new UploadPreview(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-upload-preview]').forEach((root) => {
      root.uploadPreviewInstance?.destroy();
      delete root.uploadPreviewInstance;
      delete root.dataset.uploadPreviewReady;
    });
  });
})();
